import React from "react";
import { makeLocalId } from "./scheduleUtils";
import { GroupedWeeklySchedule, ScheduleGroupKey, passOrdinalLabel } from "./types";

type StoreScheduleEditorProps = {
  value: GroupedWeeklySchedule;
  onChange: (next: GroupedWeeklySchedule) => void;
  disabled?: boolean;
};

const dayCardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.03)",
  padding: "14px 16px",
};

const dayHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  flexWrap: "wrap",
};

const dayNameStyle: React.CSSProperties = {
  fontSize: "15px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const switchStyle: React.CSSProperties = {
  position: "relative",
  width: "48px",
  height: "28px",
  borderRadius: "999px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  cursor: "pointer",
  flexShrink: 0,
};

const passRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap",
};

const passLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  minWidth: "120px",
};

const timeInputStyle: React.CSSProperties = {
  minHeight: "42px",
  padding: "0 12px",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  fontSize: "14px",
};

const removePassBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--border)",
  borderRadius: "10px",
  width: "32px",
  height: "32px",
  cursor: "pointer",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

const addPassBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--primary)",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: 700,
  padding: "6px 0",
};

function Switch({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      title={label}
      aria-label={label}
      style={{
        ...switchStyle,
        background: checked ? "var(--primary-soft)" : "var(--surface-alt)",
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "3px",
          left: checked ? "22px" : "3px",
          width: "20px",
          height: "20px",
          borderRadius: "999px",
          background: checked ? "var(--primary)" : "var(--text-secondary)",
          transition: "all var(--transition-base)",
        }}
      />
    </button>
  );
}

export default function StoreScheduleEditor({
  value,
  onChange,
  disabled = false,
}: StoreScheduleEditorProps) {
  const toggleGroup = (key: ScheduleGroupKey) => {
    onChange(
      value.map((group) => {
        if (group.key !== key) return group;
        const nextEnabled = !group.enabled;
        return {
          ...group,
          enabled: nextEnabled,
          passes:
            nextEnabled && group.passes.length === 0
              ? [{ localId: makeLocalId(), time: "" }]
              : group.passes,
        };
      })
    );
  };

  const addPass = (key: ScheduleGroupKey) => {
    onChange(
      value.map((group) =>
        group.key === key
          ? { ...group, passes: [...group.passes, { localId: makeLocalId(), time: "" }] }
          : group
      )
    );
  };

  const removePass = (key: ScheduleGroupKey, localId: string) => {
    onChange(
      value.map((group) =>
        group.key === key
          ? { ...group, passes: group.passes.filter((p) => p.localId !== localId) }
          : group
      )
    );
  };

  const setPassTime = (key: ScheduleGroupKey, localId: string, time: string) => {
    onChange(
      value.map((group) =>
        group.key === key
          ? {
              ...group,
              passes: group.passes.map((p) => (p.localId === localId ? { ...p, time } : p)),
            }
          : group
      )
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {value.map((group) => (
        <div key={group.key} style={dayCardStyle}>
          <div style={dayHeaderStyle}>
            <span style={dayNameStyle}>{group.label}</span>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                {group.enabled ? "Se colecta" : "No se colecta"}
              </span>
              <Switch
                checked={group.enabled}
                onChange={() => toggleGroup(group.key)}
                disabled={disabled}
                label={`Se colecta ${group.label}`}
              />
            </div>
          </div>

          {group.enabled && (
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {group.passes.map((pass, index) => (
                <div key={pass.localId} style={passRowStyle}>
                  <span style={passLabelStyle}>{passOrdinalLabel(index)}</span>

                  <input
                    type="time"
                    value={pass.time}
                    disabled={disabled}
                    onChange={(e) => setPassTime(group.key, pass.localId, e.target.value)}
                    style={timeInputStyle}
                  />

                  {group.passes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePass(group.key, pass.localId)}
                      disabled={disabled}
                      title="Quitar pasada"
                      style={removePassBtnStyle}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={() => addPass(group.key)}
                disabled={disabled}
                style={addPassBtnStyle}
              >
                + Agregar horario
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
